import FileUpload from './Home/components/FileUpload';
import Header from './Home/components/Header';
import Footer from './Home/components/Footer';
import { Link } from '@tanstack/react-router';
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { useSEO } from '../hooks/useSEO';
import RelatedGuides from '../components/RelatedGuides';

// FAQ data specific to GLTF Compressor
const gltfFaqs = [
  {
    question: "What is GLTF / GLB?",
    answer:
      "GLTF (GL Transmission Format) is an open standard file format for 3D scenes and models designed to be efficient and versatile for web and mobile applications. GLB is the binary version, packaging all assets (JSON, .bin, textures) into a single file.",
  },
  {
    question: "How does this GLTF compressor work?",
    answer:
      "This compressor analyzes the geometry, materials, and textures within your GLTF/GLB file. It applies Draco mesh compression and quantization to the geometry, removes duplicate and unused data, and resizes and re-encodes textures to reduce file size without significant visual impact.",
  },
  {
    question: "Is texture compression supported?",
    answer:
      "Yes. Textures can be resized to a maximum dimension and re-encoded as WebP, AVIF, JPEG or PNG with an adjustable quality setting. For texture-heavy models this is usually where most of the savings come from.",
  },
  {
    question: "Is there a file size limit?",
    answer:
      `The web app accepts files up to 500 MB each, and the API can handle larger files. New accounts get 1 free compression credit, no credit card required.`,
  },
];

function GltfCompressorPage() {
  useSEO({
    title: 'glTF Compressor Online – Compress glTF & GLB Files Up to 90%',
    description: 'Compress glTF and GLB files online by up to 90% with Draco geometry compression and WebP/AVIF textures. Batch uploads, presets and an API. Free credit to start.',
    keywords: 'GLTF compressor, GLB compression, 3D model optimization, gltf-transform, reduce file size, web 3D, AR VR optimization',
    canonical: 'https://compress-glb.com/gltf-compressor',
    ogImage: '/images/og/gltf-compressor.png',
    ogTitle: 'glTF Compressor Online – Compress glTF & GLB Files Up to 90%',
    ogDescription: 'Compress glTF and GLB files by up to 90% with Draco geometry compression and WebP/AVIF textures.',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "GLTF Compressor",
      "description": "Online tool to compress glTF and GLB 3D model files with Draco and WebP/AVIF texture compression",
      "url": "https://compress-glb.com/gltf-compressor/",
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "1 free compression credit for new accounts"
      }
    }
  });

  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col">

      <Header />

      <main className="flex-grow">
        <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            <span className="block">Compress GLTF files</span>
            <span className="block text-accent">up to 90%</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-xl text-muted-foreground/90">
            Slash file sizes for your GLTF files by up to 90% without compromising on quality. Powered by the popular gltf-transform library. Suitable for web, AR, and VR.
          </p>
          <div className="mt-8 max-w-md mx-auto sm:flex sm:justify-center md:mt-8 space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="rounded-lg shadow-sm">
              <Link
                to="/sign-up"
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-primary-foreground bg-primary hover:bg-primary-hover md:py-4 md:text-lg md:px-6 transition-all duration-200"
              >
                Sign up for free
              </Link>
            </div>
            <div className="rounded-lg shadow-sm">
              <a
                href="#file-upload"
                className="w-full flex items-center justify-center px-4 py-3 border border-accent text-base font-medium rounded-lg text-accent hover:bg-accent/10 md:py-4 md:text-lg md:px-6 transition-all duration-200"
              >
                Try for free
                <ChevronDownIcon className="ml-2 -mr-1 h-5 w-5" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <div id="file-upload"> 
          <FileUpload /> 
        </div>

        <section className="py-16 sm:py-24 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-center mb-12 text-foreground">Why Compress Your GLTF Files?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-muted-foreground/90">
              <div className="text-center p-6 bg-background-elevated rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                <h3 className="text-xl font-semibold mb-2 text-foreground">Faster Loading Times</h3>
                <p>Smaller files mean quicker downloads and rendering, providing a seamless experience for your users on web and mobile.</p>
              </div>
              <div className="text-center p-6 bg-background-elevated rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0-4c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0-4c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2z" /></svg>
                <h3 className="text-xl font-semibold mb-2 text-foreground">Reduced Bandwidth Costs</h3>
                <p>Save on hosting and data transfer costs by serving optimized, lightweight 3D models.</p>
              </div>
              <div className="text-center p-6 bg-background-elevated rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <h3 className="text-xl font-semibold mb-2 text-foreground">Improved User Experience</h3>
                <p>Keep users engaged with smooth, performant 3D interactions, crucial for e-commerce, games, and virtual tours.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-background-elevated border-y border-border/60">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-center mb-12 text-foreground">GLTF Compressor Features</h2>
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              <div className="relative">
                <dt>
                  <svg className="absolute h-6 w-6 text-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                  <p className="ml-9 text-lg leading-6 font-medium text-foreground">GLB & GLTF Support</p>
                </dt>
                <dd className="mt-2 ml-9 text-base text-muted-foreground/90">Compress both binary (.glb) and text-based (.gltf) formats seamlessly.</dd>
              </div>

              <div className="relative">
                <dt>
                  <svg className="absolute h-6 w-6 text-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  <p className="ml-9 text-lg leading-6 font-medium text-foreground">High Compression Ratios</p>
                </dt>
                <dd className="mt-2 ml-9 text-base text-muted-foreground/90">Achieve significant file size reduction with Draco geometry compression plus texture resizing and WebP/AVIF re-encoding.</dd>
              </div>

              <div className="relative">
                <dt>
                   <svg className="absolute h-6 w-6 text-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <p className="ml-9 text-lg leading-6 font-medium text-foreground">Visual Quality Preservation</p>
                </dt>
                <dd className="mt-2 ml-9 text-base text-muted-foreground/90">The intelligent optimization process minimizes visual loss, keeping your models crisp.</dd>
              </div>

              <div className="relative">
                <dt>
                  <svg className="absolute h-6 w-6 text-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  <p className="ml-9 text-lg leading-6 font-medium text-foreground">Secure & Private</p>
                </dt>
                <dd className="mt-2 ml-9 text-base text-muted-foreground/90">Your files are processed securely and automatically deleted after compression.</dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="bg-accent/10 border-y border-accent/20">
          <div className="max-w-3xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
              <span className="block">Want More Control?</span>
              <span className="block">Upload Larger Files & Customize Settings.</span>
            </h2>
            <p className="mt-4 text-lg leading-6 text-muted-foreground/90">
              Sign up for a free account to unlock the ability to fine-tune compression settings and process larger GLTF & GLB models.
            </p>
            <Link
              to="/sign-up"
              className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-lg text-primary-foreground bg-primary hover:bg-primary-hover sm:w-auto transition-all duration-200 shadow-sm"
            >
              Sign Up Free
            </Link>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-center mb-12 text-foreground">GLTF Compressor FAQ</h2>
            <div className="rounded-xl shadow-sm border border-border/60">
              {gltfFaqs.map((faq, index) => (
                <Disclosure key={faq.question} as="div" className={`${index !== 0 ? 'border-t border-border/60' : ''}`}>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex justify-between items-center w-full px-6 py-4 text-left text-base font-medium text-foreground bg-background-elevated hover:bg-background-soft focus:outline-none focus-visible:ring focus-visible:ring-accent/50 focus-visible:ring-opacity-75 transition-all duration-200">
                        <span>{faq.question}</span>
                        <ChevronUpIcon
                          className={`${
                            open ? "transform rotate-180" : ""
                          } w-5 h-5 text-accent transition-transform duration-200`}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel unmount={false} className="px-6 pb-4 pt-2 text-sm text-muted-foreground/90 bg-background-elevated border-t border-border/40">
                        {faq.answer}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              ))}
            </div>
          </div>
        </section>

        <RelatedGuides
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
          title="Related guides"
          slugs={['reduce-glb-file-size', 'draco-compression', 'gltf-transform-optimize']}
        />
      </main>

      <Footer />
    </div>
  );
}

export default GltfCompressorPage; 